"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFundingRequestGenerator = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const borrowingBase_1 = require("./borrowingBase");
const calculation_1 = require("./calculation");
const requestLoanMechanics_1 = require("./requestLoanMechanics");
// helper to get target funding date from list of  requests
const _getTargetFundingDate = ({ requests, requiresGlobalFundingDate, }) => {
    if (!requiresGlobalFundingDate) {
        return undefined;
    }
    (0, common_1.finleyAssert)(requests.length > 0 &&
        requests.every(({ targetFundingDate }) => targetFundingDate &&
            requests[0].targetFundingDate === targetFundingDate), 'Inconsistent target funding dates provided for requests that are required to be globally set.');
    return requests[0].targetFundingDate;
};
// generate a FRC key, can be used to look up FRC.
const generateFRCKey = (frc) => {
    return `${frc.sourceBankAccountId}-${frc.destinationBankAccountId}`;
};
const generateFRCKeyFromRecycle = (frc) => {
    return `${frc.routing.source.bankAccountId}-${frc.routing.destination.bankAccountId}`;
};
const validateSubmission = ({ drawRequests, recycleRequests, maxRecycle, }) => {
    // *** Validate recycle
    // Global
    const totalRecycle = common_1.utils.jsRound({
        value: _.sumBy(recycleRequests, (opt) => opt.requestAmount),
        precision: 2,
    });
    if (totalRecycle > maxRecycle) {
        return {
            isValid: false,
            errors: ['Invalid recycle request: larger than maximum recycle'],
        };
    }
    // Individual
    const individualRecycleTests = recycleRequests.every((recycleRequest) => {
        if (recycleRequest.requestAmount < 0) {
            return false;
        }
        if (recycleRequest.requestAmount > 0 &&
            recycleRequest.requestAmount < recycleRequest.constraints.minimum) {
            return false;
        }
        if (recycleRequest.requestAmount > 0 &&
            recycleRequest.requestAmount > recycleRequest.constraints.maximum) {
            return false;
        }
        // 0 here, true
        return true;
    });
    if (!individualRecycleTests) {
        return { isValid: false, errors: ['Invalid recycle amount'] };
    }
    // *** Validate draw
    // Global
    const totalDraw = common_1.utils.jsRound({
        value: _.sumBy(drawRequests, (opt) => opt.requestAmount),
        precision: 2,
    });
    const globalMinimum = drawRequests[0]?.constraints.minimum ?? 0;
    if (totalDraw > 0 && totalDraw < globalMinimum) {
        return {
            isValid: false,
            errors: [
                `Invalid draw request amount - required to be either $0 or an amount greater than the minimum draw: $${globalMinimum.toLocaleString()}`,
            ],
        };
    }
    // Individual
    const individualDrawTests = drawRequests.every((drawRequest) => {
        if (drawRequest.requestAmount < 0) {
            return false;
        }
        if (drawRequest.requestAmount > 0 &&
            drawRequest.requestAmount < drawRequest.constraints.minimum) {
            return false;
        }
        if (drawRequest.requestAmount > 0 &&
            drawRequest.requestAmount > drawRequest.constraints.maximum) {
            return false;
        }
        // 0 here, true
        return true;
    });
    if (!individualDrawTests) {
        return {
            isValid: false,
            errors: [
                'Invalid draw request amounts. Please ensure that no negative values are present and also that maximum constraints are respected.',
            ],
        };
    }
    return { isValid: true };
};
/**
 * ***** Initialization *****
 * 1. Retrieve template inputs from server
 *    > `const inputs = await server.getFundingRequestTemplate();`
 *    - Inputs are static after retrieval - no further server interaction required
 *    - Expected inputs:
 *      - Funding mechanics (determined by server, transformed from CA)
 *      - Borrowing base report (null if not available)
 *      - Outstanding balances (by commitment)
 *      - Cash balances (data pipeline output, empty if not available)
 *      - Pending requests (draw/paydown/recycle)
 *
 * 2. Get funding request using inputs
 *    > `const fundingRequest = funding.getFundingRequest(inputs);`
 *
 *
 * ***** Data Architecture *****
 *   1) Summary (no change or interaction required)
 *        - Calculated by template logic using inputs
 *   2) Request form (requires interaction, with side effects)
 *        - Pricing options array (key/label)
 *        - Recycle options array, for each:
 *            - Initial amount (default value, does not change)
 *            - Request amount
 *            - Constraints (with valid/invalid state): minimum, increment (optional), maximum
 *        - Draw options array (key/label) for dropdown (draw options map to class commitments), for each:
 *            - Initial amount (default value, does not change)
 *            - Request amount
 *            - Interest rates (all in rate, base rate, margin rate)
 *            - Constraints (with valid/invalid state): minimum, increment (optional), maximum
 *        - Target funding date input
 *   3) Expected submission (recalculated after updates)
 *        - Recycle requests array
 *            - Total amount
 *            - 1 row per recycle option containing columns:
 *                - Source bank account
 *                - Destination bank account
 *                - Amount
 *            - Additional constraints (maximum amount per period and maximum frequency)
 *        - Draw requests array
 *            - Total amount
 *            - 1 row per draw option containing:
 *                - Total amount
 *                    - 1 row per lender within draw option containing:
 *                        - Lender
 *                        - Source bank account
 *                        - Destination bank account
 *                        - Pro-rata percent
 *                        - Amount
 *            - Additional constraints (maximum amount per period and maximum frequency)
 *   4) Funding impact (recalculated after updates)
 *        - Commitment (no change)
 *        - Borrowing base: total, cash, receivables
 *        - Maximum available amount
 *        - Outstanding balance
 *        - Draw availability
 *        - Recycle availability
 *
 * ***** Client Interaction *****
 * Note on linked equations:
 *   Draw and recycle options are inherently linked when governed by borrowing base availability.
 *   User inputs in request form should not automatically adjust up/down.
 *   Instead, the minimum/maximum constraints will dynamically adjust based on related calculations.
 *   If the user requests a larger amount for one option that impacts other options, the constraint will be marked as failing.
 *
 * At this time, the reserve is fixed according to the maximum conservative estimate.
 *
 * For each action, a list of side effects to associated getters will be returned by each function.
 * The client should use this list to update the displays accordingly.
 *
 * Action: Set pricing option (interest rate)
 *    > `fundingRequest.pricingOptions.set({ key: 'key' })`
 * Impact: pricing option, draw options
 *
 * Action: Set recycle amount (interest rate)
 *    > `fundingRequest.recycleRequests.set({ key: 'key', amount: 123.45 })`
 * Impact: recycle options, draw options, and funding impact
 *
 * Action: Set draw amount (interest rate)
 *    > `fundingRequest.drawRequests.set({ key: 'key', amount: 123.45 })`
 * Impact: recycle options, draw options, and funding impact
 *
 * Action: Set target funding date (interest rate)
 *    > `fundingRequest.targetFundingDate.set('2024-10-15')`
 * Impact: target funding date, draw options
 *
 * Action: Submit request
 * 1) Validate ability to submit request (this can be used to govern the state of the 'submit' button)
 *    > `const { isValid } = fundingRequest.submission.getState();`
 * 2) Format submission to send data that the server requires for processing
 *    > `const submissionPayload = fundingRequest.submission.getPayload();`
 * 3) Result will be { success: true; } | { success: false; message: string }
 *    > `const result = await server.submitFundingRequest(submissionPayload);`
 *
 * Notable simplification (to be modified):
 * - Does not consider recycle that returns to bank account that contributes to borrowing base (to be handled in upcoming update)
 *
 */
const getFundingRequestGenerator = (params) => {
    if (params.fundingMechanicsV2Serialized) {
        return (0, requestLoanMechanics_1.getFundingRequestGeneratorWithLoanMechanics)(params);
    }
    // everyone other than those on the v2 serialized mechanics can't upsize (so everyone other than valley sls)
    (0, common_1.finleyAssert)(params.mode !== 'upsize', new common_1.error.FinleyError('Upsizing loans not supported', {
        logLevel: common_1.error.LogLevel.Error,
        metadata: {
            logMeta: {
                creditFacilityId: params.creditFacilityId,
                mode: params.mode,
            },
        },
    }));
    (0, common_1.finleyAssert)(params.fundingMechanics, `Funding mechanics must be provided for request`);
    const { fundingMechanics, borrowingBaseReport, cashBalances: startingCash, outstandingBalances: startingBalances, pendingRequests, currentFundingRequestComponents, borrowingBaseReportMetadataForLastApprovedFundingRequest, allowedActions, } = params;
    (0, common_1.finleyAssert)(borrowingBaseReport, 'Borrowing base report must exist');
    // ***** PRO FORMA BORROWING BASE *****
    // Generate pro forma borrowing base: borrowing base report, cash, outstanding, pending requests
    const { borrowingBaseReport: proformaBorrowingBaseReport, outstandingBalances, cashBalances, } = (0, borrowingBase_1.getBorrowingBase)({
        borrowingBaseReport,
        cashBalances: startingCash,
        outstandingBalances: startingBalances,
        pendingRequests: allowedActions === 'readOnly' ? [] : pendingRequests,
        requestRoutings: fundingMechanics.requestRoutings,
    });
    // ***** BORROWING BASE DEFICIENCY *****
    // Calculate total deficiency => Redirect to repayment flow
    const availability = (0, calculation_1.getBorrowingBaseAvailability)({
        outstandingBalance: proformaBorrowingBaseReport.currentPrincipalBalance,
        receivableAdvance: proformaBorrowingBaseReport.maxReceivableAdvanceAmount,
        cashBalances,
        borrowingBaseReserveAmount: proformaBorrowingBaseReport.borrowingBaseReserveAmount ?? 0,
    });
    // Calculate receivable deficiency => Restrict draw
    // Perform initial allocations, set up recycle and draw options
    // ***** RECYCLE AVAILABILITY *****
    // forces maxRecycle to 0 if negative
    const recycleRequestsAreGlobalFundingDate = true; // @jendevelops TODO: update from configuration on CA
    const recycleRequests = [];
    // Round reserve up to provide maximum reserve
    const cashReserveAmount = common_1.utils.jsRound({
        value: borrowingBaseReport.cashReserveAmount ?? 0,
        precision: 2,
        mode: 'ceiling',
    });
    let maxRecycle = Math.max((0, calculation_1.getMaxRecycle)({
        cashBalances,
        cashReserveAmount,
    }), 0);
    // Round principal balance up to maximize current utilization (and thereby reduce potential for exceeding availability)
    const proformaPrincipalBalance = common_1.utils.jsRound({
        value: proformaBorrowingBaseReport.currentPrincipalBalance,
        precision: 2,
        mode: 'ceiling',
    });
    // Round receivable advance down to minimize potential for unintended availability
    const maxReceivableAdvanceAmount = common_1.utils.jsRound({
        value: proformaBorrowingBaseReport.maxReceivableAdvanceAmount,
        precision: 2,
        mode: 'floor',
    });
    // Cash coverage of receivable deficiency (cannot remove cash that is required to support the borrowing base)
    // This deficiency figure is only used to reduce the max recycle, so it is floored at 0
    const receivableDeficiency = Math.max(common_1.utils.jsRound({
        value: proformaPrincipalBalance - maxReceivableAdvanceAmount,
        precision: 2,
    }), 0);
    // Max recycle has already accounted above for reduction according to cash reserve
    // Max recycle equation does not need to intrinsically know about this concept of cash coverage (additional assets are outside the scope of the account itself)
    // This reduction is applied after the account has been calculated (balance - reserve)
    maxRecycle = Math.max(common_1.utils.jsRound({
        value: maxRecycle - receivableDeficiency,
        precision: 2,
    }), 0);
    const singleRecycle = fundingMechanics.recycles.length === 1;
    fundingMechanics.recycles.forEach((fundingMechanicsRecycle) => {
        const newlyAssignedEligExpConstraint = fundingMechanicsRecycle.constraints.find((constraint) => constraint.type === 'condition' &&
            constraint.condition ===
                'limitToNewlyAssignedEligibleExposureSinceLastApprovedFundingRequest');
        if (newlyAssignedEligExpConstraint &&
            newlyAssignedEligExpConstraint.value === 1 // true
        ) {
            // Round assigned eligible exposure down to prevent exceeding allowed recycle
            const assignedEligibleExposureSinceLastApprovedFundingRequest = typeof borrowingBaseReport.assignedEligibleExposureSinceLastApprovedFundingRequest ===
                'number'
                ? common_1.utils.jsRound({
                    value: borrowingBaseReport.assignedEligibleExposureSinceLastApprovedFundingRequest,
                    precision: 2,
                    mode: 'floor',
                })
                : Infinity;
            maxRecycle = Math.min(maxRecycle, assignedEligibleExposureSinceLastApprovedFundingRequest);
        }
        const availableBalanceContainer = cashBalances.find((cashBalance) => cashBalance.bankAccountId ===
            fundingMechanicsRecycle.routing.source.bankAccountId);
        (0, common_1.finleyAssert)(availableBalanceContainer, `Unable to find cash balance for recycle request: ${fundingMechanicsRecycle.routing.source.bankAccountId}`);
        // this is always in USD
        const availableBalance = availableBalanceContainer.balance;
        // but underlying currency could be different
        // if so provide details.
        const fxDetails = availableBalanceContainer.currencyCode === 'USD'
            ? null
            : {
                currencyCode: availableBalanceContainer.currencyCode,
                exchangeRate: availableBalanceContainer.conversionRate,
            };
        const recycleComponent = currentFundingRequestComponents.find((component) => component.id === fundingMechanicsRecycle.fundingRequestComponentId &&
            component.destinationBankAccountId ===
                fundingMechanicsRecycle.routing.destination.bankAccountId &&
            component.sourceBankAccountId ===
                fundingMechanicsRecycle.routing.source.bankAccountId);
        (0, common_1.finleyAssert)(recycleComponent, 'Unable to find recycle funding request component');
        const frcKey = generateFRCKey(recycleComponent);
        const maxRecycleForAccount = Math.min(maxRecycle, availableBalance);
        // if read only mode, only show the recorded amount (no suggestions)
        const initialRecycleRequestAmount = params.allowedActions === 'readOnly'
            ? recycleComponent.amount
            : maxRecycleForAccount;
        const name = singleRecycle
            ? 'Collections recycle'
            : `${fundingMechanicsRecycle.routing.source.label}`;
        recycleRequests.push({
            key: frcKey,
            name,
            initialAmount: maxRecycleForAccount,
            requestAmount: initialRecycleRequestAmount,
            fxDetails,
            targetFundingDate: recycleComponent.targetFundingDate ||
                fundingMechanicsRecycle.targetFundingDate, // @jendevelops TODO update to individual funding dates if configured in CA
            constraints: {
                minimum: 0,
                maximum: maxRecycleForAccount,
                increment: 0,
            },
        });
    });
    // ***** PRICING OPTIONS *****
    // ***** DRAW AVAILABILITY *****
    // @jendevelops need to update when can configure individual funding dates per share
    const targetDrawFundingDate = currentFundingRequestComponents.find((currComponent) => currComponent.type ===
        finley_types_1.FundingRequest.FundingRequestComponentType.DrawRequest)?.targetFundingDate || fundingMechanics.draw.targetFundingDate;
    const maxFunding = availability;
    const drawRequestsAreGlobalFundingDate = true; // @jendevelops TODO: update from configuration on CA
    const drawRequests = [];
    const availabilityAfterRecycle = (0, calculation_1.getBorrowingBaseAvailability)({
        outstandingBalance: proformaBorrowingBaseReport.currentPrincipalBalance,
        receivableAdvance: proformaBorrowingBaseReport.maxReceivableAdvanceAmount,
        cashBalances,
        borrowingBaseReserveAmount: proformaBorrowingBaseReport.borrowingBaseReserveAmount ?? 0,
        recycleRequest: maxRecycle,
    });
    let priorAttachmentRate = 0;
    const maxAttachmentRate = fundingMechanics.commitments[fundingMechanics.commitments.length - 1]
        ?.attachmentRate ?? 1;
    for (const commitment of fundingMechanics.commitments) {
        const classAttachmentRate = common_1.utils.divideByOrZero(commitment.attachmentRate - priorAttachmentRate, maxAttachmentRate);
        // Calculate class level share
        const maxAllocation = common_1.utils.jsRound({
            value: classAttachmentRate * availabilityAfterRecycle,
            precision: 2,
        });
        const minimumConstraint = fundingMechanics.draw.constraints.find((constraint) => constraint.type === 'minimum')?.value;
        const minimum = typeof minimumConstraint === 'number'
            ? Math.max(fundingMechanics.draw.allocation.strict
                ? minimumConstraint * classAttachmentRate
                : minimumConstraint, 0) // makes sure that minimumConstraint can't be negative
            : 0;
        const organizationIds = commitment.shares.map((share) => share.lenderId);
        const rawClassBalance = _.sumBy(outstandingBalances.filter((outstandingBalance) => organizationIds.includes(outstandingBalance.organizationId)), (balance) => balance.amount);
        const classBalance = common_1.utils.jsRound({
            value: rawClassBalance,
            precision: 2,
        });
        const unused = common_1.utils.jsRound({
            value: commitment.amount - classBalance,
            precision: 2,
        });
        const drawRequestAmount = commitment.shares.reduce((acc, share) => {
            const component = currentFundingRequestComponents.find((currComponent) => currComponent.id === share.fundingRequestComponentId);
            (0, common_1.finleyAssert)(component, `Unable to find funding request component for lender [id: ${share.lenderId}]`);
            return acc + component.amount;
        }, 0);
        // find the minimum between maxAllocation and the unused amount
        // then if the minimum is less than 0, set the starting draw amount to 0
        const initialDrawAmount = Math.max(Math.min(maxAllocation, unused), 0);
        // if the calc agent is in read only mode, don't suggest a draw amount, just return the recorded amount
        const startingRequestAmount = params.allowedActions === 'readOnly'
            ? drawRequestAmount
            : initialDrawAmount;
        drawRequests.push({
            key: commitment.key,
            name: commitment.label,
            initialAmount: initialDrawAmount,
            fxDetails: null,
            requestAmount: startingRequestAmount,
            targetFundingDate: targetDrawFundingDate, // @jendevelops TODO update to individual funding dates if configured in CA
            constraints: {
                minimum,
                maximum: initialDrawAmount,
                increment: null,
            },
            attachmentLimit: classAttachmentRate,
            pricingOption: {
                key: fundingMechanics.draw.pricingOptions[0].key,
                name: fundingMechanics.draw.pricingOptions[0].name,
            },
            loan: null, // Unused
        });
        // Store current attachment rate for next iteration
        priorAttachmentRate = commitment.attachmentRate;
    }
    const updateDrawRequests = ({ request, updateAmount = true, }) => {
        const recycleRequest = recycleRequests.reduce((acc, curr) => acc + curr.requestAmount, 0);
        const localavailabilityAfterRecycle = (0, calculation_1.getBorrowingBaseAvailability)({
            outstandingBalance: proformaBorrowingBaseReport.currentPrincipalBalance,
            receivableAdvance: proformaBorrowingBaseReport.maxReceivableAdvanceAmount,
            cashBalances,
            borrowingBaseReserveAmount: proformaBorrowingBaseReport.borrowingBaseReserveAmount ?? 0,
            recycleRequest,
        });
        let requestAmount = Infinity;
        if (request && request.key) {
            const attachmentLimit = drawRequests.find(({ key }) => request.key === key)?.attachmentLimit ??
                0;
            requestAmount = common_1.utils.divideByOrZero(request.amount, attachmentLimit);
        }
        else if (request) {
            requestAmount = request.amount;
        }
        let localpriorAttachmentRate = 0;
        for (const drawRequest of drawRequests) {
            const commitment = fundingMechanics.commitments.find((loopcommitment) => loopcommitment.key === drawRequest.key);
            (0, common_1.finleyAssert)(commitment, 'Unable to update draw request input');
            // Calculate class level share
            const classAttachmentRate = (commitment.attachmentRate - localpriorAttachmentRate) /
                maxAttachmentRate;
            // prevent negative values
            const maxAllocation = Math.max(common_1.utils.jsRound({
                value: classAttachmentRate * localavailabilityAfterRecycle,
                precision: 2,
            }), 0);
            const maxRequestAmount = common_1.utils.jsRound({
                value: classAttachmentRate * requestAmount,
                precision: 2,
            });
            const organizationIds = commitment.shares.map((share) => share.lenderId);
            const rawClassBalance = _.sumBy(outstandingBalances.filter((outstandingBalance) => organizationIds.includes(outstandingBalance.organizationId)), (balance) => balance.amount);
            const classBalance = common_1.utils.jsRound({
                value: rawClassBalance,
                precision: 2,
            });
            const unused = common_1.utils.jsRound({
                value: commitment.amount - classBalance,
                precision: 2,
            });
            drawRequest.constraints.maximum = Math.min(maxAllocation, unused);
            // Store current attachment rate for next iteration
            localpriorAttachmentRate = commitment.attachmentRate;
            // After calculating class contribution, skip if (a) not in strict mode and (b) not targeting this draw input (class A/B, etc.)
            if (!fundingMechanics.draw.allocation.strict && // Not strict means do whatever you want to this input lol
                drawRequest.key !== request?.key // Only skip for other inputs, i.e. update class A only means skip class B
            ) {
                continue;
            }
            // Recycle may impact draw maximum, but draw amount set by user should not change (just show invalid based on new constraint)
            if (updateAmount) {
                drawRequest.requestAmount = Math.min(maxAllocation, unused, maxRequestAmount);
            }
        }
    };
    // Calculate constraints (draw max set to 0 with insufficient cash:  < 0)
    // ***** SUMMARY DATA *****
    const totalCommitment = _.sumBy(fundingMechanics.commitments, (commitment) => commitment.amount);
    const totalOutstanding = proformaBorrowingBaseReport.currentPrincipalBalance;
    // ***** MATURITY DATE *****
    let maturityDate = common_1.date.addMonths(common_1.date.currentDateStr(), 1);
    // ***** EXTERNAL LOAN ID *****
    let externalLoanId;
    // ***** LOAN TYPE *****
    let loanType = 'origination';
    return {
        deficiency: Math.max(-1 * availability, 0), // When availability is negative, it is treated as deficiency
        summary: {
            commitment: { amount: totalCommitment, currency: 'USD' },
            // can't use totalBorrowingBaseAmount because it's no longer accurate
            // db records for totalBorrowingBaseAmount need to be migrated before use @danitian
            borrowingBaseAmount: (borrowingBaseReport.maxReceivableAdvanceAmount ?? 0) +
                (borrowingBaseReport.totalCashBorrowingBaseAmount ?? 0),
            maximumAvailableAmount: borrowingBaseReport.maximumAvailableAmount ?? 0,
            outstandingBalance: totalOutstanding,
            facilityUtilization: totalOutstanding / totalCommitment,
            pendingRequests: pendingRequests.map((pendingRequest) => ({
                fundingRequestId: pendingRequest.fundingRequestId,
                fundingRequestComponentId: pendingRequest.id,
                type: pendingRequest.type,
                amount: pendingRequest.amount,
                targetFundingDate: pendingRequest.targetFundingDate,
            })),
            drawAvailability: maxFunding,
            recycleAvailability: maxRecycle,
            borrowingBaseReportMetadata: {
                reportingDate: borrowingBaseReport.reportingDate,
                fileVersion: borrowingBaseReport.fileVersion,
            },
            assignedEligibleExposureSinceLastApprovedFundingRequest: borrowingBaseReport.assignedEligibleExposureSinceLastApprovedFundingRequest,
            borrowingBaseReportMetadataForLastApprovedFundingRequest,
        },
        data: {
            pricingOptions: {
                get: () => {
                    return fundingMechanics.draw.pricingOptions;
                },
                set: (request) => {
                    // set pricing option for all draw requests (global pricing option)
                    if (!request.drawRequestKey) {
                        drawRequests.forEach((drawRequest) => {
                            // eslint-disable-next-line no-param-reassign
                            drawRequest.pricingOption.key = request.pricingOptionKey;
                        });
                    }
                    else {
                        // set price for individual draw request
                        const drawRequest = drawRequests.find((opt) => opt.key === request.drawRequestKey);
                        (0, common_1.finleyAssert)(drawRequest, `Invalid draw request option (${request.drawRequestKey}) selected for update.`);
                        drawRequest.pricingOption.key = request.pricingOptionKey;
                    }
                    // Reset maturity date default
                    if (request.pricingOptionKey.endsWith('1M')) {
                        maturityDate = common_1.date.addMonths(targetDrawFundingDate, 1);
                    }
                    if (request.pricingOptionKey.endsWith('3M')) {
                        maturityDate = common_1.date.addMonths(targetDrawFundingDate, 3);
                    }
                    return ['drawRequests', 'pricingOptions', 'maturityDate'];
                },
            },
            maturityDate: {
                get: () => {
                    return maturityDate;
                },
                set: ({ date }) => {
                    maturityDate = date;
                    return ['maturityDate'];
                },
            },
            externalLoanId: {
                get: () => {
                    return externalLoanId;
                },
                set: (input) => {
                    externalLoanId = input.externalLoanId ?? undefined;
                    return ['externalLoanId'];
                },
            },
            loanType: {
                get: () => {
                    return loanType;
                },
                set: (input) => {
                    loanType = input.loanType;
                    return ['loanType'];
                },
            },
            resolvedLoan: {
                get: () => undefined,
            },
            recycleRequests: {
                isGlobalFundingDate: recycleRequestsAreGlobalFundingDate,
                get: () => {
                    return recycleRequests;
                },
                set: (request) => {
                    const recycleRequest = recycleRequests.find((option) => option.key === request.key);
                    (0, common_1.finleyAssert)(recycleRequest, `Invalid recycle option (${request.key}) selected for update.`);
                    recycleRequest.requestAmount = request.amount;
                    updateDrawRequests({ updateAmount: false });
                    return [
                        'drawRequests',
                        'expectedSubmission',
                        'fundingImpact',
                        'recycleRequests',
                        'netRecycleRequest',
                    ];
                },
            },
            netRecycleRequest: {
                get: () => {
                    if (recycleRequests.length <= 1) {
                        // net recycle request is applicable
                        // only when we have multiple recycle requests
                        return null;
                    }
                    const requestAmount = recycleRequests.reduce((acc, curr) => acc + curr.requestAmount, 0);
                    return {
                        key: 'NetRecycleRequest',
                        name: 'NetRecycleRequest',
                        initialAmount: 0,
                        requestAmount,
                        fxDetails: null,
                        targetFundingDate: recycleRequests[0].targetFundingDate,
                        constraints: {
                            minimum: 0,
                            maximum: maxRecycle,
                            increment: 0,
                        },
                    };
                },
                set: () => {
                    // this is read only.
                    return [];
                },
            },
            drawRequests: {
                isGlobalFundingDate: drawRequestsAreGlobalFundingDate, // @jendevelops TODO: update from configuration on CA
                get: () => {
                    return drawRequests;
                },
                set: (request) => {
                    // Global
                    if (!request.key) {
                        updateDrawRequests({
                            request: { amount: request.amount },
                        });
                        return [
                            'drawRequests',
                            'expectedSubmission',
                            'fundingImpact',
                            'recycleRequests',
                        ];
                    }
                    // Individual
                    const drawRequest = drawRequests.find((option) => option.key === request.key);
                    (0, common_1.finleyAssert)(drawRequest, `Invalid draw request option (${request.key}) selected for update.`);
                    updateDrawRequests({
                        request: { key: drawRequest.key, amount: request.amount },
                    });
                    return [
                        'drawRequests',
                        'expectedSubmission',
                        'fundingImpact',
                        'recycleRequests',
                    ];
                },
            },
            targetFundingDate: {
                set: (request) => {
                    if (request.key) {
                        switch (request.option) {
                            case 'draw': {
                                const drawRequest = drawRequests.find((opt) => opt.key === request.key);
                                (0, common_1.finleyAssert)(drawRequest, `Invalid draw request option (${request.key}) selected for update.`);
                                drawRequest.targetFundingDate = request.targetFundingDate;
                                return ['drawRequests'];
                            }
                            case 'recycle': {
                                const recycleRequest = recycleRequests.find((opt) => opt.key === request.key);
                                (0, common_1.finleyAssert)(recycleRequest, `Invalid recycle request option (${request.key}) selected for update.`);
                                recycleRequest.targetFundingDate = request.targetFundingDate;
                                return ['recycleRequests'];
                            }
                            default:
                                return (0, common_1.finleyAssertUnreachable)(request.option);
                        }
                    }
                    else {
                        // no key, set global funding date (all draw/recycle requests the same date)
                        switch (request.option) {
                            case 'draw': {
                                drawRequests.forEach((drawRequest) => {
                                    // eslint-disable-next-line no-param-reassign
                                    drawRequest.targetFundingDate = request.targetFundingDate;
                                });
                                return ['drawRequests'];
                            }
                            case 'recycle': {
                                recycleRequests.forEach((recycleRequest) => {
                                    // eslint-disable-next-line no-param-reassign
                                    recycleRequest.targetFundingDate = request.targetFundingDate;
                                });
                                return ['recycleRequests'];
                            }
                            default:
                                return (0, common_1.finleyAssertUnreachable)(request.option);
                        }
                    }
                },
            },
            fundingImpact: {
                get: () => {
                    return {
                        commitment: { amount: totalCommitment, currency: 'USD' },
                        borrowingBaseAmount: {
                            cash: {
                                before: borrowingBaseReport.maxCashAdvanceAmount,
                                after: borrowingBaseReport.maxCashAdvanceAmount,
                            },
                            receivables: {
                                before: borrowingBaseReport.maxReceivableAdvanceAmount,
                                after: borrowingBaseReport.maxReceivableAdvanceAmount,
                            },
                            total: {
                                before: borrowingBaseReport.borrowingBaseAvailability ?? 0,
                                after: borrowingBaseReport.borrowingBaseAvailability ?? 0,
                            },
                        },
                        maximumAvailableAmount: {
                            before: borrowingBaseReport.maximumAvailableAmount ?? 0,
                            after: borrowingBaseReport.maximumAvailableAmount ?? 0,
                        },
                        outstandingBalance: {
                            before: totalOutstanding,
                            after: totalOutstanding +
                                drawRequests.reduce((sum, drawRequest) => sum + drawRequest.requestAmount, 0),
                        },
                        drawAvailability: { before: 0, after: 0 },
                        recycleAvailability: { before: 0, after: 0 },
                    };
                },
            },
            expectedSubmission: {
                get: () => {
                    const drawSubmissions = [];
                    for (const commitment of fundingMechanics.commitments) {
                        const drawRequest = drawRequests.find((opt) => opt.key === commitment.key);
                        (0, common_1.finleyAssert)(drawRequest, `Unable to find draw request for ${commitment.label}`);
                        drawSubmissions.push({
                            key: commitment.key,
                            name: commitment.label,
                            amount: drawRequest.requestAmount,
                            loanKey: 'none',
                            pricingOptionKey: drawRequest.pricingOption.key,
                            shares: commitment.shares.map((share) => ({
                                fundingRequestComponentId: share.fundingRequestComponentId,
                                lender: {
                                    organizationId: share.lenderId,
                                    name: share.lenderName,
                                },
                                bankAccounts: {
                                    source: share.routing.source.label,
                                    destination: share.routing.destination.label,
                                },
                                percent: share.amount / commitment.amount,
                                amount: common_1.utils.jsRound({
                                    value: (share.amount / commitment.amount) *
                                        drawRequest.requestAmount,
                                    precision: 2,
                                }),
                                applicableMargin: share.applicableMargin,
                                projectedInterest: 0, // @sachinr4 TODO: update with projected interest
                            })),
                            maturityDate, // Unused
                            loanType: 'origination', // Unused
                        });
                    }
                    const recycleSubmissions = fundingMechanics.recycles.map((recycle) => {
                        const recycleRequest = recycleRequests.find((opt) => opt.key === generateFRCKeyFromRecycle(recycle));
                        (0, common_1.finleyAssert)(recycleRequest, `Unable to find recycle request for ${recycle.routing.source.label}`);
                        return {
                            key: recycle.fundingRequestComponentId,
                            amount: recycleRequest.requestAmount,
                            sourceBankAccount: recycle.routing.source.label,
                            destinationBankAccount: recycle.routing.destination.label,
                        };
                    });
                    const allDrawSubmissionPricingOptionsSame = drawSubmissions.every((option) => option.pricingOptionKey === drawSubmissions[0]?.pricingOptionKey);
                    return {
                        recycle: {
                            global: {
                                requestAmount: common_1.utils.jsRound({
                                    value: _.sumBy(recycleRequests, (opt) => opt.requestAmount),
                                    precision: 2,
                                }),
                                constraints: [],
                                targetFundingDate: fundingMechanics.recycles.length > 0
                                    ? _getTargetFundingDate({
                                        requests: recycleRequests,
                                        requiresGlobalFundingDate: recycleRequestsAreGlobalFundingDate,
                                    })
                                    : undefined,
                            },
                            requests: recycleSubmissions,
                        },
                        draw: {
                            global: {
                                requestAmount: common_1.utils.jsRound({
                                    value: _.sumBy(drawRequests, (opt) => opt.requestAmount),
                                    precision: 2,
                                }),
                                constraints: [],
                                // @jendevelops place holder to set a global pricing option value until we can determine if pricing option should be set per draw or for all draws
                                pricingOptionKey: allDrawSubmissionPricingOptionsSame
                                    ? drawSubmissions[0].pricingOptionKey
                                    : undefined,
                                targetFundingDate: _getTargetFundingDate({
                                    requests: drawRequests,
                                    requiresGlobalFundingDate: drawRequestsAreGlobalFundingDate,
                                }),
                            },
                            requests: drawSubmissions,
                        },
                    };
                },
            },
        },
        submission: {
            getPayload: () => {
                const validationResult = validateSubmission({
                    drawRequests,
                    recycleRequests,
                    maxRecycle,
                });
                if (!validationResult.isValid) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: validationResult.errors,
                    };
                }
                const fundingRequestComponents = [];
                // Add draws
                // @jendevelops TODO: placeholder for getting targetFundingDate from drawRequests until we can determine if targetFundingDate should be set per draw or for all draws
                const drawRequestTargetFundingDate = drawRequests[0]?.targetFundingDate;
                if (!drawRequestTargetFundingDate) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: ['Draw request must have target funding date'],
                    };
                }
                for (const commitment of fundingMechanics.commitments) {
                    commitment.shares.forEach((share) => {
                        const drawRequest = drawRequests.find((opt) => opt.key === commitment.key);
                        (0, common_1.finleyAssert)(drawRequest, `Unable to find draw request for ${commitment.label}`);
                        const proRataAllocation = (share.amount / commitment.amount) * drawRequest.requestAmount;
                        fundingRequestComponents.push({
                            id: share.fundingRequestComponentId,
                            type: finley_types_1.FundingRequest.FundingRequestComponentType.DrawRequest,
                            amount: proRataAllocation,
                            targetFundingDate: drawRequestTargetFundingDate,
                            sourceBankAccountId: share.routing.source.bankAccountId,
                            destinationBankAccountId: share.routing.destination.bankAccountId,
                            applicableMargin: share.applicableMargin,
                        });
                    });
                }
                // Add recycle
                // @jendevelops TODO: placeholder for getting targetFundingDate from drawRequests until we can determine if targetFundingDate should be set per recycle or for all recycles
                const recycleRequestTargetFundingDate = recycleRequests.length > 0
                    ? recycleRequests[0].targetFundingDate
                    : drawRequestTargetFundingDate; // Use this as a fallback if no recycles are present, this has no impact on the backend, just FE issue with unblocking submission from the client
                if (!recycleRequestTargetFundingDate) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: ['Recycle request must have target funding date'],
                    };
                }
                fundingMechanics.recycles.forEach((fundingMechanicsRecycle) => {
                    // find corresponding recycle request
                    const recycleRequest = recycleRequests.find((opt) => opt.key === generateFRCKeyFromRecycle(fundingMechanicsRecycle));
                    (0, common_1.finleyAssert)(recycleRequest, `Unable to find recycle request for ${fundingMechanicsRecycle.routing.source.label}`);
                    fundingRequestComponents.push({
                        id: fundingMechanicsRecycle.fundingRequestComponentId,
                        type: finley_types_1.FundingRequest.FundingRequestComponentType.CollectionsRecycle,
                        amount: recycleRequest.requestAmount, // Fixed assumption of 1 option
                        targetFundingDate: recycleRequestTargetFundingDate,
                        sourceBankAccountId: fundingMechanicsRecycle.routing.source.bankAccountId,
                        destinationBankAccountId: fundingMechanicsRecycle.routing.destination.bankAccountId,
                    });
                });
                return {
                    isValid: true,
                    payload: {
                        apiVersion: 2,
                        id: fundingMechanics.fundingRequestId,
                        fundingRequestComponents,
                        loanRequests: [],
                    },
                };
            },
        },
    };
};
exports.getFundingRequestGenerator = getFundingRequestGenerator;
//# sourceMappingURL=request.js.map